import React, { useState } from "react";
import { payWithRazorpay } from "../lib/razorpay";
import { toast } from "sonner";
import { Zap, Loader2, X, QrCode } from "lucide-react";

const PRESETS = [100, 250, 500, 1000, 2000];
const RATE = 10; // ₹10 = 1 AI listing

/**
 * Self-serve credits recharge — ₹10 per AI listing. Pay any amount via
 * Razorpay (UPI QR / card / netbanking) and credits auto-activate
 * instantly, no admin needed.
 */
export function RechargeBox({ onSuccess, compact = false }) {
  const [amount, setAmount] = useState(500);
  const [paying, setPaying] = useState(false);
  const listings = Math.floor((Number(amount) || 0) / RATE);

  async function pay() {
    if (listings < 1) {
      toast.error("Minimum ₹10 (1 listing)");
      return;
    }
    setPaying(true);
    try {
      const res = await payWithRazorpay({ recharge_amount_inr: Number(amount) });
      toast.success(`${listings} listings activated! Invoice ${res.invoice_number || ""}`);
      onSuccess?.(res);
    } catch (e) {
      const msg = e?.message || "Payment failed";
      if (msg !== "Payment cancelled") toast.error(msg);
    } finally {
      setPaying(false);
    }
  }

  return (
    <div data-testid="recharge-box">
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p) => (
          <button
            key={p}
            onClick={() => setAmount(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
              Number(amount) === p
                ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                : "bg-white text-[#535665] border-[#E9E9ED] hover:border-[#4F46E5]"
            }`}
            data-testid={`recharge-preset-${p}`}
          >
            ₹{p}
            <span className="block text-[9px] font-medium opacity-80">{p / RATE} listings</span>
          </button>
        ))}
      </div>
      <div className={`flex ${compact ? "flex-col" : "flex-row"} gap-2 items-stretch`}>
        <div className="flex-1 flex items-center gap-2 border border-[#E9E9ED] rounded-lg px-3 py-2 bg-white">
          <span className="text-sm font-bold text-[#94969F]">₹</span>
          <input
            type="number"
            min="10"
            step="10"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full text-sm font-bold text-[#0B0C12] outline-none"
            data-testid="recharge-amount-input"
          />
          <span className="text-[11px] font-bold text-[#03A685] whitespace-nowrap">
            = {listings} listing{listings === 1 ? "" : "s"}
          </span>
        </div>
        <button
          onClick={pay}
          disabled={paying || listings < 1}
          className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-[#03A685] hover:bg-[#02936F] text-white text-sm font-bold disabled:opacity-60"
          data-testid="recharge-pay-btn"
        >
          {paying ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={14} />}
          Pay &amp; Activate
        </button>
      </div>
      <div className="text-[10px] text-[#94969F] mt-2">
        ₹{RATE} = 1 AI Auto Listing · UPI QR / Card / Netbanking · Credits activate instantly after payment
      </div>
    </div>
  );
}

export function RechargeModal({ open, onClose, onSuccess }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="recharge-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white border border-[#EDEDF1] shadow-2xl p-6 text-[#0B0C12]"
      >
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-[#03A685]/10 grid place-items-center">
              <Zap size={16} className="text-[#03A685]" />
            </span>
            <div>
              <div className="font-bold text-lg">Recharge AI Listings</div>
              <div className="text-[11px] text-[#94969F]">₹10 per listing · pay any amount, activate instantly</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F5F5F8] text-[#94969F]" data-testid="recharge-close">
            <X size={15} />
          </button>
        </div>
        <div className="mt-4">
          <RechargeBox compact onSuccess={(r) => { onSuccess?.(r); onClose?.(); }} />
        </div>
      </div>
    </div>
  );
}
